import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline title editor: shows title + pencil icon.
 * When pencil clicked, becomes an editable input with Save (✓) and Cancel (✗) buttons.
 * onSave(newTitle) returns a Promise; component handles busy state and rollback.
 */
const InlineTitleEdit = ({ value, onSave, className = '', inputClassName = '', dataTestId = 'inline-title', placeholder = 'Titolo…' }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const cancel = () => { setText(value || ''); setEditing(false); };
  const save = async () => {
    const t = text.trim();
    if (!t) return toast.error('Il titolo non può essere vuoto');
    if (t === value) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(t); setEditing(false); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Errore salvataggio'); setText(value || ''); }
    finally { setBusy(false); }
  };
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  if (!editing) {
    return (
      <div className={`group inline-flex items-center gap-2 ${className}`}>
        <span data-testid={`${dataTestId}-value`}>{value}</span>
        <button
          type="button"
          onClick={()=>setEditing(true)}
          data-testid={`${dataTestId}-edit-btn`}
          className="opacity-40 hover:opacity-100 transition-opacity text-current hover:text-[#C9A961] p-1 rounded-full"
          title="Rinomina"
        >
          <Pencil className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <input
        ref={inputRef}
        value={text}
        onChange={e=>setText(e.target.value)}
        onKeyDown={onKey}
        disabled={busy}
        placeholder={placeholder}
        data-testid={`${dataTestId}-input`}
        className={`bg-white/10 border border-[#C9A961]/60 focus:border-[#C9A961] outline-none rounded-lg px-3 py-1 min-w-[220px] ${inputClassName}`}
      />
      <button type="button" onClick={save} disabled={busy} data-testid={`${dataTestId}-save-btn`} className="p-1.5 rounded-full bg-[#C9A961] hover:bg-white text-black transition-colors disabled:opacity-50" title="Salva">
        <Check className="w-4 h-4" />
      </button>
      <button type="button" onClick={cancel} disabled={busy} data-testid={`${dataTestId}-cancel-btn`} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-current transition-colors" title="Annulla">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InlineTitleEdit;
